import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { App as AntdApp, ConfigProvider } from 'antd';
import { useI18n } from '@/i18n/useI18n';
import { buildAntdTheme } from '@/theme/antdTheme';
import { applyThemeVars, resolveOverride, resolveTheme } from '@/theme/cssVars';
import { useHubTheme } from '@/theme/ThemeContext';
import type { ThemeMode } from '@/theme/tokens';

/**
 * Provides the AntD theme + `--ol-*` variables + writing direction above the whole tree. The applicant
 * surface has NO manual theme toggle — it follows the OS (`prefers-color-scheme`), defaulting to light.
 * The RTL `direction` is driven by the active locale (Arabic → rtl), so AntD components mirror to match
 * the document's `dir`.
 *
 * P1: a hub's published theme (fetched once the session is active) merges its Palette overrides
 * over the mode defaults — both into the CSS variables and the AntD config. No theme ⇒ both
 * paths are byte-identical to the pre-theming build.
 */
export function ThemedRoot({ children }: { children: ReactNode }) {
  const { dir } = useI18n();
  const { theme: hubTheme } = useHubTheme();
  const [mode, setMode] = useState<ThemeMode>(resolveTheme);

  const override = useMemo(
    () => resolveOverride(hubTheme?.tokens, mode),
    [hubTheme, mode],
  );

  useEffect(() => {
    applyThemeVars(mode, override);
  }, [mode, override]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => setMode(resolveTheme());
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const design = hubTheme?.brand?.design;
  const theme = useMemo(() => buildAntdTheme(mode, override, design), [mode, override, design]);

  return (
    <ConfigProvider theme={theme} direction={dir}>
      <AntdApp style={{ height: '100%' }}>{children}</AntdApp>
    </ConfigProvider>
  );
}
